import { usePromptPreferencesBase } from "@/hooks/use-prompt-preferences-base";
import { useApi } from "@/hooks/useApi";
import { notifyFavoritesChanged, PROMPT_EVENTS } from "@/lib/prompt-events";
import { getPromptId } from "@/lib/prompt-utils";
import {
  PromptDTO,
  ToggleFavoritePromptDTO,
  UserPromptsPreferencesDTO,
} from "@shaarp/common";
import { useCallback, useEffect, useState } from "react";

type UseFavoritePromptsOptions = {
  fetchCustomPrompts?: boolean;
  listenForChanges?: boolean;
};

type UseFavoritePromptsReturn = {
  favoriteIds: Set<string>;
  favoritePrompts: PromptDTO[];
  customPrompts: PromptDTO[];
  toggleFavorite: (prompt: PromptDTO) => Promise<boolean>;
  isFavorite: (prompt: PromptDTO) => boolean;
  refreshFavorites: () => Promise<void>;
  isLoading: boolean;
};

export function useFavoritePrompts(
  options: UseFavoritePromptsOptions = {},
): UseFavoritePromptsReturn {
  const { fetchCustomPrompts = false, listenForChanges = true } = options;
  const api = useApi();
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [favoritePrompts, setFavoritePrompts] = useState<PromptDTO[]>([]);

  const onPreferencesFetched = useCallback(
    (data: UserPromptsPreferencesDTO): void => {
      setFavoriteIds(new Set(data.favoritePromptIds));
    },
    [],
  );

  const { customPrompts, isLoadingPreferences, fetchPreferences } =
    usePromptPreferencesBase({
      fetchCustomPrompts,
      listenForChanges,
      onPreferencesFetched,
    });

  const isLoading = isLoadingPreferences;

  const computeFavoritePrompts = useCallback(
    (ids: Set<string>, prompts: PromptDTO[]): PromptDTO[] => {
      return prompts.filter((prompt) => ids.has(getPromptId(prompt)));
    },
    [],
  );

  // Update favorite prompts when favoriteIds or customPrompts change
  useEffect(() => {
    setFavoritePrompts(computeFavoritePrompts(favoriteIds, customPrompts));
  }, [favoriteIds, customPrompts, computeFavoritePrompts]);

  // Listen for favorites changes from other components
  useEffect(() => {
    if (!listenForChanges) return;

    const handleFavoritesChanged = (
      event: CustomEvent<{ favoriteIds: string[] }>,
    ) => {
      setFavoriteIds(new Set(event.detail.favoriteIds));
    };

    window.addEventListener(
      PROMPT_EVENTS.FAVORITES_CHANGED,
      handleFavoritesChanged as EventListener,
    );
    return () => {
      window.removeEventListener(
        PROMPT_EVENTS.FAVORITES_CHANGED,
        handleFavoritesChanged as EventListener,
      );
    };
  }, [listenForChanges]);

  const refreshFavorites = useCallback(async () => {
    await fetchPreferences();
  }, [fetchPreferences]);

  const isFavorite = useCallback(
    (prompt: PromptDTO): boolean => {
      return favoriteIds.has(getPromptId(prompt));
    },
    [favoriteIds],
  );

  const toggleFavorite = useCallback(
    async (prompt: PromptDTO): Promise<boolean> => {
      const promptId = getPromptId(prompt);

      const { data } = await api.post<ToggleFavoritePromptDTO>(
        `/prompts/favorites/${promptId}`,
      );

      const newIds = new Set(favoriteIds);
      if (data.isFavorite) {
        newIds.add(promptId);
      } else {
        newIds.delete(promptId);
      }

      setFavoriteIds(newIds);

      notifyFavoritesChanged([...newIds]);

      return data.isFavorite;
    },
    [api, favoriteIds],
  );

  return {
    favoriteIds,
    favoritePrompts,
    customPrompts,
    toggleFavorite,
    isFavorite,
    refreshFavorites,
    isLoading,
  };
}
